import api from './api';

export interface UnifiedRole {
  id: string;
  role_name: string;
  display_name: string;
  description?: string;
  role_type: 'PLATFORM' | 'TENANT';
  scope_type: 'PLATFORM' | 'TENANT';
  tenant_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  permissions?: UnifiedRolePermission[];
}

export interface UnifiedRolePermission {
  id: string;
  role_id: string;
  role_type: 'PLATFORM' | 'TENANT';
  scope_type: 'PLATFORM' | 'TENANT';
  resource_type: 'MODULE' | 'NAVIGATION_SCREEN' | 'NODE';
  resource_id: string;
  permission_type: 'READ' | 'CREATE' | 'UPDATE' | 'DELETE' | 'EXECUTE' | 'APPROVE' | 'EXPORT' | 'IMPORT' | 'ARCHIVE' | 'CONFIGURE';
  is_active: boolean;
  created_at: string;
}

export interface UnifiedRoleAssignment {
  id: string;
  user_id: string;
  role_id: string;
  assigned_by?: string;
  assigned_at: string;
  status: 'ACTIVE' | 'INACTIVE';
  role_name?: string;
  display_name?: string;
  role_type?: 'PLATFORM' | 'TENANT';
  scope_type?: 'PLATFORM' | 'TENANT';
}

export interface UserPermission {
  role_id: string;
  role_name: string;
  resource_type: string;
  resource_id: string;
  permission_type: string;
  is_inherited: boolean;
}

export interface NavigationModule {
  id: string;
  module_code: string;
  display_name: string;
  description?: string;
  icon?: string;
  translation_key?: string;
  sort_order: number;
  is_active: boolean;
  parent_module_id?: string;
  path?: string; // For navigation purposes
  children: NavigationScreen[];
}

export interface NavigationScreen {
  id: string;
  screen_code: string;
  display_name: string;
  description?: string;
  icon?: string;
  translation_key?: string;
  sort_order: number;
  is_active: boolean;
  module_id: string;
  route_path?: string;
  path?: string; // For navigation purposes
  has_read_permission: boolean;
  has_configure_permission: boolean;
  /** True when plan does not include this capability (gray out in sidebar). */
  lacks_capability?: boolean;
}

export interface NavigationTree {
  modules: NavigationModule[];
}

export interface NavigationData {
  modules: NavigationModule[];
  screens: NavigationScreen[];
}

export class UnifiedRBACService {
  // Role Management
  async getAllRoles(): Promise<UnifiedRole[]> {
    const response = await api.get('/rbac/roles');
    return response.data.data;
  }

  async getRoleById(roleId: string): Promise<UnifiedRole> {
    const response = await api.get(`/rbac/roles/${roleId}`);
    return response.data.data;
  }

  async getCurrentUserRoles(): Promise<UnifiedRoleAssignment[]> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      throw new Error('User not authenticated');
    }
    const response = await api.get(`/rbac/users/${currentUserId}/roles`);
    return response.data.data || [];
  }

  async createRole(roleData: Omit<UnifiedRole, 'id' | 'created_at' | 'updated_at'>): Promise<UnifiedRole> {
    const response = await api.post('/rbac/roles', roleData);
    return response.data.data;
  }

  async updateRole(roleId: string, updates: Partial<UnifiedRole>): Promise<UnifiedRole> {
    const response = await api.put(`/rbac/roles/${roleId}`, updates);
    return response.data.data;
  }

  async deleteRole(roleId: string): Promise<void> {
    await api.delete(`/rbac/roles/${roleId}`);
  }

  // Permission Management
  async getRolePermissions(roleId: string): Promise<UnifiedRolePermission[]> {
    const response = await api.get(`/rbac/roles/${roleId}/permissions`);
    return response.data.data;
  }

  async saveRolePermissions(roleId: string, permissions: Omit<UnifiedRolePermission, 'id' | 'created_at'>[]): Promise<void> {
    await api.post(`/rbac/roles/${roleId}/permissions`, { permissions });
  }

  // User Permission Resolution
  async getUserPermissions(userId: string): Promise<UserPermission[]> {
    const response = await api.get(`/rbac/users/${userId}/permissions`);
    return response.data.data;
  }

  async checkUserPermission(
    userId: string,
    resourceType: string,
    resourceId: string,
    permissionType: string
  ): Promise<boolean> {
    const response = await api.post(`/rbac/users/${userId}/permissions/check`, {
      resourceType,
      resourceId,
      permissionType
    });
    return response.data.data.hasPermission;
  }

  /**
   * Check if a navigation screen exists in the system
   * This checks if a route is registered in the navigation system
   */
  async checkScreenExists(
    routePath?: string,
    screenCode?: string
  ): Promise<{ exists: boolean; screenId?: string; screenCode?: string; routePath?: string; isPublic?: boolean }> {
    const response = await api.post('/rbac/screen/exists', {
      routePath,
      screenCode
    });
    return response.data.data;
  }

  /**
   * Check if user has permission on a navigation screen by screen code
   * This is used for route protection when user accesses URL directly
   */
  async checkScreenPermissionByCode(
    userId: string,
    screenCode: string,
    permissionType: string = 'READ'
  ): Promise<boolean> {
    const response = await api.post(`/rbac/users/${userId}/screen-permission/check`, {
      screenCode,
      permissionType
    });
    return response.data.data.hasPermission;
  }

  async getUserNavigationPermissions(userId: string): Promise<NavigationTree> {
    const response = await api.get(`/rbac/users/${userId}/navigation-permissions`);
    return response.data.data;
  }

  // Role Assignment Management
  async getUserRoleAssignments(userId: string): Promise<UnifiedRoleAssignment[]> {
    const response = await api.get(`/rbac/users/${userId}/role-assignments`);
    return response.data.data;
  }

  async assignRoleToUser(userId: string, roleId: string): Promise<UnifiedRoleAssignment> {
    const response = await api.post('/rbac/users/role-assignments', {
      userId,
      roleId
    });
    return response.data.data;
  }

  async removeRoleFromUser(userId: string, roleId: string): Promise<void> {
    await api.delete(`/rbac/users/${userId}/role-assignments/${roleId}`);
  }

  // Navigation Management
  async getAllNavigationData(): Promise<NavigationData> {
    const response = await api.get('/rbac/navigation/items');
    return response.data.data;
  }

  // Legacy method - keeping for backward compatibility but should be replaced
  async getNavigationItems(): Promise<NavigationData> {
    console.warn('getNavigationItems() is deprecated. Use getAllNavigationData() instead.');
    return this.getAllNavigationData();
  }

  async getUserNavigation(userId: string, category: string = 'BUSINESS'): Promise<NavigationTree> {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now();
    const response = await api.get(`/rbac/users/${userId}/navigation-permissions`, {
      params: { category: category, t: timestamp }
    });
    
    
    return response.data.data;
  }

  async getManagementNavigationHierarchy(): Promise<NavigationTree> {
    // Add cache-busting parameter to ensure fresh data
    const timestamp = Date.now();
    const response = await api.get(`/navigation/management-hierarchy?t=${timestamp}`);
    return response.data.data;
  }

  async moveNavigationItem(moveRequest: {
    itemId: string;
    itemType: 'module' | 'screen';
    newParentId?: string;
    newSortOrder: number;
  }): Promise<void> {
    await api.post('/rbac/navigation/items/move', moveRequest);
  }

  async createNavigationItem(createRequest: {
    itemType: 'module' | 'screen';
    displayName: string;
    parentModuleId?: string;
    sortOrder: number;
    icon?: string;
    description?: string;
    routePath?: string;
  }): Promise<{ id: string }> {
    const response = await api.post('/rbac/navigation/items', createRequest);
    return response.data.data;
  }

  async deleteNavigationItem(itemId: string, itemType: 'module' | 'screen'): Promise<void> {
    await api.delete(`/rbac/navigation/items/${itemType}/${itemId}`);
  }

  async updateNavigationItem(
    itemId: string, 
    itemType: 'module' | 'screen' | 'node', 
    updates: Partial<NavigationModule | NavigationScreen | any>
  ): Promise<void> {
    const response = await api.put(`/rbac/navigation/items/${itemType}/${itemId}`, updates);
    
    // Check if the response indicates an error
    if (response.data && response.data.success === false) {
      throw new Error(response.data.error || response.data.message || 'Update failed');
    }
  }

  // Helper methods for frontend
  async getCurrentUserPermissions(): Promise<UserPermission[]> {
    // This would need to be implemented based on how you get the current user ID
    // For now, we'll assume it's available from auth context
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      throw new Error('User not authenticated');
    }
    return this.getUserPermissions(currentUserId);
  }

  async getCurrentUserNavigation(category: string = 'BUSINESS'): Promise<NavigationTree> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      throw new Error('User not authenticated');
    }
    return this.getUserNavigation(currentUserId, category);
  }

  private getCurrentUserId(): string | null {
    // Get user ID from localStorage
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || null;
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }
    return null;
  }

  // Permission checking helpers
  hasPermission(permissions: UserPermission[], resourceType: string, resourceId: string, permissionType: string): boolean {
    return permissions.some(perm => 
      perm.resource_type === resourceType &&
      perm.resource_id === resourceId &&
      perm.permission_type === permissionType
    );
  }

  hasNavigationPermission(navigationTree: NavigationTree, path: string, permissionType: 'READ' | 'CONFIGURE'): boolean {
    // Search through all modules and their screens
    for (const module of navigationTree.modules) {
      for (const screen of module.children) {
        if (screen.route_path === path) {
          return permissionType === 'READ' ? screen.has_read_permission : screen.has_configure_permission;
        }
      }
    }
    return false;
  }

  // Role filtering helpers
  filterRolesByType(roles: UnifiedRole[], roleType: 'PLATFORM' | 'TENANT'): UnifiedRole[] {
    return roles.filter(role => role.role_type === roleType);
  }

  filterRolesByScope(roles: UnifiedRole[], scopeType: 'PLATFORM' | 'TENANT'): UnifiedRole[] {
    return roles.filter(role => role.scope_type === scopeType);
  }

  // Permission grouping helpers
  groupPermissionsByResource(permissions: UnifiedRolePermission[]): Record<string, UnifiedRolePermission[]> {
    return permissions.reduce((groups, permission) => {
      const key = `${permission.resource_type}:${permission.resource_id}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(permission);
      return groups;
    }, {} as Record<string, UnifiedRolePermission[]>);
  }

  groupPermissionsByType(permissions: UnifiedRolePermission[]): Record<string, UnifiedRolePermission[]> {
    return permissions.reduce((groups, permission) => {
      if (!groups[permission.permission_type]) {
        groups[permission.permission_type] = [];
      }
      groups[permission.permission_type].push(permission);
      return groups;
    }, {} as Record<string, UnifiedRolePermission[]>);
  }
}

export const unifiedRBACService = new UnifiedRBACService();
